import React, { useEffect, useState, useMemo } from 'react';
import './ProductList.css';
import { useCart } from './CartContext';
import CartControls from './CartControls.jsx';

const API_URL = (process.env.REACT_APP_API_URL || 'https://api.advaytraders.in').replace(/\/$/, '');

const getImageUrl = (imagePath, updatedAt) => {
  if (!imagePath) return `${API_URL}/uploads/AD-preview.png`;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
  const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
  const normalizedPath = cleanPath.replace(/\\/g, '/');
  const cacheBust = updatedAt ? `?t=${new Date(updatedAt).getTime()}` : '';
  return `${API_URL}/${normalizedPath}${cacheBust}`;
};

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const { cartItems, addToCart } = useCart();
  const [categories, setCategories] = useState([]);

  // Search, Filter & Sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('default');

  useEffect(() => {
    // Fetch categories and products from backend API
    Promise.all([
      fetch(`${API_URL}/api/categories`).then(res => res.json()).catch(() => []),
      fetch(`${API_URL}/api/products`).then(res => res.json())
    ])
      .then(([catsData, prodsData]) => {
        if (Array.isArray(catsData)) {
          setCategories(catsData.map(c => c.name));
        }

        if (Array.isArray(prodsData)) {
          // Filter only active products and map to the format expected by the component
          const productList = prodsData
            .filter(product => product.isActive)
            .map(product => ({
              id: product._id,
              'Product Name': product.name,
              Category: product.category?.name || 'Uncategorized',
              img: getImageUrl(product.image, product.updatedAt),
              'Original Price': product.originalPrice,
              'Discount Price': product.offerPrice,
              order: product.order !== undefined && product.order !== null ? product.order : 0,
              Unit: '1 Pkt' // Default unit
            }));
          setProducts(productList);
        }
      })
      .catch((err) => console.error('Failed to load store products:', err));
  }, []);

  // Clean admin-ordered categories
  const cleanCategories = useMemo(() => categories.map(c => (c || '').trim()), [categories]);

  // Unique categories from products for the category filter dropdown
  const availableCategories = useMemo(() => {
    const catsSet = new Set();
    products.forEach(p => {
      const c = (p.Category || '').trim();
      if (c) catsSet.add(c);
    });

    const ordered = cleanCategories.filter(c => catsSet.has(c));
    catsSet.forEach(c => {
      if (!ordered.includes(c)) ordered.push(c);
    });
    return ordered;
  }, [products, cleanCategories]);

  // Filter products by search term and selected category
  const filteredProducts = useMemo(() => {
    let list = products;

    // Filter by search term
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase().trim();
      list = list.filter(p =>
        (p['Product Name'] && p['Product Name'].toLowerCase().includes(q)) ||
        (p.Category && p.Category.toLowerCase().includes(q))
      );
    }

    // Filter by Category
    if (selectedCategory !== 'All') {
      list = list.filter(p => (p.Category || '').trim() === selectedCategory.trim());
    }

    return list;
  }, [products, searchTerm, selectedCategory]);

  // Group filtered products by Category
  const groupedProducts = useMemo(() => {
    return filteredProducts.reduce((acc, product) => {
      const category = product.Category?.trim() || 'Uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(product);
      return acc;
    }, {});
  }, [filteredProducts]);

  // Dynamic category order to display
  const allGroupCategories = Object.keys(groupedProducts);
  const categoriesToDisplay = [
    ...cleanCategories.filter(cat => groupedProducts[cat]),
    ...allGroupCategories.filter(cat => !cleanCategories.includes(cat))
  ];

  // Sorter for items within each category
  const sortCategoryItems = (items) => {
    const list = items.slice();
    switch (sortBy) {
      case 'price-low-high':
        return list.sort((a, b) => {
          const priceA = parseFloat(a['Discount Price'] || a['Original Price'] || 0);
          const priceB = parseFloat(b['Discount Price'] || b['Original Price'] || 0);
          return priceA - priceB;
        });
      case 'price-high-low':
        return list.sort((a, b) => {
          const priceA = parseFloat(a['Discount Price'] || a['Original Price'] || 0);
          const priceB = parseFloat(b['Discount Price'] || b['Original Price'] || 0);
          return priceB - priceA;
        });
      case 'name-a-z':
        return list.sort((a, b) => (a['Product Name'] || '').localeCompare(b['Product Name'] || ''));
      case 'name-z-a':
        return list.sort((a, b) => (b['Product Name'] || '').localeCompare(a['Product Name'] || ''));
      case 'default':
      default:
        return list.sort((a, b) => {
          const orderA = a.order !== undefined && a.order !== null ? a.order : 0;
          const orderB = b.order !== undefined && b.order !== null ? b.order : 0;
          if (orderA !== orderB) return orderA - orderB;
          return String(a.id || '').localeCompare(String(b.id || ''));
        });
    }
  };

  const isFiltered = Boolean(searchTerm.trim() || selectedCategory !== 'All' || sortBy !== 'default');

  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('All');
    setSortBy('default');
  };

  let serialNumber = 1;

  return (
    <div className="product-list-page">
      {/* Top Filter & Sort Bar above Table */}
      <div className="filter-sort-bar-wrapper">
        <div className="filter-sort-bar">
          {/* Search by Product */}
          <div className="filter-group search-group">
            <span className="search-icon" aria-hidden="true">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              className="filter-search-input"
              placeholder="Search product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search product"
            />
            {searchTerm && (
              <button
                type="button"
                className="filter-clear-icon-btn"
                onClick={() => setSearchTerm('')}
                title="Clear search"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="filter-group select-group">
            <label className="filter-label" htmlFor="cat-filter-select">Category:</label>
            <select
              id="cat-filter-select"
              className="filter-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              aria-label="Filter by Category"
            >
              <option value="All">All Categories ({products.length})</option>
              {availableCategories.map((cat) => {
                const count = products.filter(p => (p.Category || '').trim() === cat).length;
                return (
                  <option key={cat} value={cat}>
                    {cat} ({count})
                  </option>
                );
              })}
            </select>
          </div>

          {/* Sort By Dropdown */}
          <div className="filter-group select-group">
            <label className="filter-label" htmlFor="sort-select">Sort by:</label>
            <select
              id="sort-select"
              className="filter-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort products"
            >
              <option value="default">Default Order</option>
              <option value="price-low-high">Price: Low to High</option>
              <option value="price-high-low">Price: High to Low</option>
              <option value="name-a-z">Product: A to Z</option>
              <option value="name-z-a">Product: Z to A</option>
            </select>
          </div>

          {/* Reset button when active */}
          {isFiltered && (
            <button
              type="button"
              className="filter-reset-btn"
              onClick={handleResetFilters}
              title="Reset all filters"
            >
              <i className="fas fa-undo-alt me-1"></i> Reset
            </button>
          )}

          {/* Counter badge */}
          <div className="filter-count-badge">
            <strong>{filteredProducts.length}</strong> / {products.length} Items
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="product-table-container">
        {filteredProducts.length === 0 ? (
          <div className="no-products-box">
            <p className="no-products-text">No products found matching your search.</p>
            <button
              type="button"
              className="filter-reset-btn"
              onClick={handleResetFilters}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="product-table">
              <thead>
                <tr>
                  <th className="th-sno">S.No</th>
                  <th className="th-image">Image</th>
                  <th className="th-product">Product</th>
                  <th className="th-pack">Pack</th>
                  <th className="th-price">Price</th>
                  <th className="th-cart">Cart</th>
                  <th className="th-qty">Qty</th>
                </tr>
              </thead>
              <tbody>
                {categoriesToDisplay.map((category) => {
                  const items = sortCategoryItems(groupedProducts[category] || []);
                  if (items.length === 0) return null;

                  return (
                    <React.Fragment key={category}>
                      <tr>
                        <td colSpan="7" className="category-row">
                          {category}
                        </td>
                      </tr>
                      {items.map((p) => {
                        const cartItem = cartItems.find((i) => i._id === p.id);
                        const productName = p['Product Name'] || 'Unnamed Product';
                        const productImage = p.img || getImageUrl(null);

                        return (
                          <tr key={p.id}>
                            <td className="td-sno">{serialNumber++}</td>
                            <td className="td-image">
                              <a
                                href={productImage}
                                data-fancybox="gallery"
                                data-caption={productName}
                              >
                                <img
                                  src={productImage}
                                  alt={productName}
                                  className="product-img"
                                  loading="lazy"
                                />
                              </a>
                            </td>
                            <td className="td-product">{productName}</td>
                            <td className="td-pack">{p.Unit || '1 Pkt'}</td>
                            <td className="td-price">
                              {parseFloat(p['Original Price'] || 0) > 0 && parseFloat(p['Discount Price'] || 0) > 0 && (
                                <span className="original-price">
                                  ₹{parseFloat(p['Original Price']).toFixed(2)}
                                </span>
                              )}
                              <span className="discounted-price">
                                ₹{parseFloat(
                                  p['Discount Price'] ||
                                  p['Original Price'] ||
                                  0
                                ).toFixed(2)}
                              </span>
                            </td>
                            <td className="td-cart">
                              {cartItem ? (
                                <CartControls item={cartItem} />
                              ) : (
                                <button
                                  className="add-btn"
                                  onClick={() =>
                                    addToCart({
                                      ...p,
                                      _id: p.id,
                                      name: p['Product Name'],
                                      originalPrice: parseFloat(p['Original Price'] || 0),
                                      offerPrice: parseFloat(p['Discount Price'] || p['Original Price'] || 0),
                                      price: parseFloat(p['Discount Price'] || p['Original Price'] || 0),
                                      discountPrice: parseFloat(p['Discount Price'] || 0),
                                    })
                                  }
                                >
                                  Add
                                </button>
                              )}
                            </td>
                            <td className="td-qty">{cartItem?.quantity || 0}</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;
